/**
 * TechsupportTab — v1 user-facing tab (issue #351).
 *
 * WhatsApp-style bubbles (shared MessageBubble), date splitters,
 * source_url capture, IntersectionObserver-driven read-on-view that
 * fires ``POST /chat/threads/{id}/read`` debounced by ~250ms with the
 * latest visible message_id as the watermark.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Send } from 'lucide-react';
import {
  useChatMessages,
  useChatThreads,
  useMarkChatRead,
  usePostChatMessage,
} from '../../../hooks/useChatQueries';
import { useChat } from '../ChatContext';
import { MessageBubble } from '../MessageBubble';
import { withDateSplitters } from '../DateSplitter';
import { useAutoGrowTextarea } from '../../../hooks/useAutoGrowTextarea';
import type { ChatMessage } from '../../../services/chatApi';
import './TechsupportTab.css';
import '../MessageBubble.css';

export interface TechsupportTabProps {
  isOpen: boolean;
  /** Bumped by ``ChatProvider`` each time ``openChat({ prefill })`` is
   * called so we know to drain ``consumePrefill`` and seed the draft. */
  prefillVersion?: number;
}

const READ_DEBOUNCE_MS = 250;

export const TechsupportTab: React.FC<TechsupportTabProps> = ({
  isOpen,
  prefillVersion = 0,
}) => {
  const { t } = useTranslation();
  const threadsQuery = useChatThreads();
  // Select the human-support thread BY KIND. ``useChatThreads`` now returns
  // multiple kinds (human_support, agent_navigator, agent_reader,
  // agent_assistant); a position-based pick (``data[0]``) leaked the private
  // KI-Hofassistent conversation into this tab and misrouted outbound support
  // requests to the assistant thread once it sorted to index 0 (issue #1677).
  // Mirrors ``KiAssistantTab``'s ``.find((tr) => tr.thread_kind === ...)``.
  const thread = useMemo(
    () => threadsQuery.data?.find((tr) => tr.thread_kind === 'human_support'),
    [threadsQuery.data],
  );
  const messagesQuery = useChatMessages(thread?.thread_id);
  const post = usePostChatMessage(thread?.thread_id);
  const markRead = useMarkChatRead(thread?.thread_id);
  const { consumePrefill } = useChat();
  const [draft, setDraft] = useState('');
  const [pendingSourceApp, setPendingSourceApp] = useState<string | null>(null);
  const textareaRef = useAutoGrowTextarea(draft, { minRows: 1, maxRows: 5 });
  const containerRef = useRef<HTMLDivElement | null>(null);
  const bubbleRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const observerRef = useRef<IntersectionObserver | null>(null);
  const debounceRef = useRef<number | null>(null);
  const lastReadRef = useRef<string | null>(null);

  // Drain the pending prefill from ChatProvider whenever a new one
  // arrives (``prefillVersion`` bumps). Seeds the draft and parks the
  // cursor at the literal ``…`` placeholder so the user lands ready
  // to type the request.
  useEffect(() => {
    if (prefillVersion === 0 || !isOpen) return;
    const pending = consumePrefill();
    if (!pending) return;
    if (pending.prefill) {
      setDraft(pending.prefill);
      // Defer cursor placement to the next tick so the textarea has
      // re-rendered with the new value.
      requestAnimationFrame(() => {
        const ta = textareaRef.current;
        if (!ta || !pending.prefill) return;
        const placeholderIdx = pending.prefill.lastIndexOf('…');
        if (placeholderIdx >= 0) {
          ta.focus();
          ta.setSelectionRange(placeholderIdx, placeholderIdx + 1);
        } else {
          ta.focus();
          ta.setSelectionRange(pending.prefill.length, pending.prefill.length);
        }
      });
    }
    setPendingSourceApp(pending.sourceApp ?? null);
  }, [prefillVersion, isOpen, consumePrefill]);

  // Oldest-first so date splitters appear chronologically. Soft-deleted
  // messages are filtered out on the user side per the subtask-09
  // contract: the admin sees the tombstone (and the audit log keeps
  // the original body), the user simply stops seeing the row at all.
  const messagesOldestFirst = useMemo(() => {
    if (!messagesQuery.data) return [] as ChatMessage[];
    return messagesQuery.data
      .filter((m) => !m.deleted_at)
      .slice()
      .sort(
        (a, b) => new Date(a.sent_at).getTime() - new Date(b.sent_at).getTime(),
      );
  }, [messagesQuery.data]);

  // The Support tab badge is sourced directly from ``useChatThreads()`` in
  // ``ChatProvider`` (#1735) — the server-side per-user ``unread_count`` LEFT
  // JOIN (``chat_message_reads``, migration 0022) — so this tab no longer
  // pushes its mount-dependent counter up the tree. ``useMarkChatRead`` below
  // invalidates ``chatKeys.threads`` on read, shrinking the badge with no
  // client-side reconciliation.

  // Debounced mark-read: fire ~250ms after the latest visible message
  // settles in the viewport.
  const scheduleMarkRead = useCallback(
    (messageId: string) => {
      if (lastReadRef.current === messageId) return;
      if (debounceRef.current !== null) {
        window.clearTimeout(debounceRef.current);
      }
      debounceRef.current = window.setTimeout(() => {
        lastReadRef.current = messageId;
        markRead.mutate(messageId);
        debounceRef.current = null;
      }, READ_DEBOUNCE_MS);
    },
    [markRead],
  );

  // IntersectionObserver: fire mark-read for the highest-watermark
  // bubble currently visible while the panel is open.
  useEffect(() => {
    if (!isOpen || typeof IntersectionObserver === 'undefined') return;

    const container = containerRef.current;
    if (!container) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        let bestMessageId: string | null = null;
        let bestSentAt = 0;
        for (const entry of entries) {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.5) continue;
          const el = entry.target as HTMLElement;
          const id = el.dataset.messageId;
          const sentAt = Number(el.dataset.sentAtMs ?? 0);
          if (id && sentAt >= bestSentAt) {
            bestMessageId = id;
            bestSentAt = sentAt;
          }
        }
        if (bestMessageId) scheduleMarkRead(bestMessageId);
      },
      { root: container, threshold: [0.5] },
    );

    for (const node of bubbleRefs.current.values()) {
      observerRef.current.observe(node);
    }
    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      if (debounceRef.current !== null) {
        window.clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, [isOpen, scheduleMarkRead, messagesOldestFirst]);

  const registerBubble = useCallback(
    (msg: ChatMessage, node: HTMLDivElement | null) => {
      const map = bubbleRefs.current;
      if (node === null) {
        map.delete(msg.message_id);
        return;
      }
      node.dataset.messageId = msg.message_id;
      node.dataset.sentAtMs = String(new Date(msg.sent_at).getTime());
      map.set(msg.message_id, node);
      observerRef.current?.observe(node);
    },
    [],
  );

  const handleSend = () => {
    const trimmed = draft.trim();
    if (!trimmed || !thread) return;
    // ``source_url`` tagging convention:
    //   - ``app://<id>``: message originated from the #945 Feedback flow
    //     on the matching /apps tile. The admin tool filters/groups on
    //     this so feature feedback rolls up per app.
    //   - regular URL: routine widget send — capture the route the user
    //     was on (already in place since subtask 04).
    const sourceUrl = pendingSourceApp
      ? `app://${pendingSourceApp}`
      : `${window.location.pathname}${window.location.search}`;
    post.mutate(
      { body: trimmed, sourceUrl },
      {
        onSuccess: () => {
          setDraft('');
          setPendingSourceApp(null);
        },
      },
    );
  };

  if (!isOpen) return null;

  return (
    <div className="chat-techsupport">
      <div
        ref={containerRef}
        className="chat-techsupport__messages"
        data-testid="chat-messages"
      >
        {messagesOldestFirst.length === 0 && !messagesQuery.isLoading && (
          <p className="chat-techsupport__empty">
            {t('chat.empty.user', 'Send us a message — we usually reply within one working day.')}
          </p>
        )}
        {withDateSplitters(messagesOldestFirst, (msg) => (
          <div
            key={msg.message_id}
            ref={(node) => registerBubble(msg, node)}
            className="chat-techsupport__bubble-wrap"
            data-author-kind={msg.author_kind}
          >
            <MessageBubble message={msg} viewerKind="user" />
          </div>
        ))}
      </div>
      <form
        className="chat-techsupport__composer"
        onSubmit={(e) => {
          e.preventDefault();
          handleSend();
        }}
      >
        <div className="chat-techsupport__input-wrap">
          <textarea
            ref={textareaRef}
            className="chat-techsupport__input"
            placeholder={t('chat.input.placeholder', 'Write a message…')}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            rows={1}
          />
          <button
            type="submit"
            className="chat-techsupport__send"
            aria-label={t('chat.input.send', 'Send')}
            title={t('chat.input.send', 'Send')}
            disabled={post.isPending || !draft.trim() || !thread}
          >
            <Send size={18} />
          </button>
        </div>
      </form>
    </div>
  );
};
